import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { SurveyService } from '../../../service/survey.service';
import { MessageService } from 'src/app/service/message.service';
import { SurveyNewPrevidenceRequest } from 'src/app/model/survey-request';
import { NdgDataEmit } from 'src/app/model/survey-emits';
import { familyIncomeSourceIncongruence } from 'src/app/utils/incongruence';
import { SurveySavingsResponse } from 'src/app/model/survey-response';

@Component({
  selector: 'previdence',
  templateUrl: './previdence.component.html'
})
export class PrevidenceComponent implements OnChanges {
  @Input() surveyId!: string;
  @Input() ndgData!: NdgDataEmit;
  @Input() previdence?: SurveySavingsResponse;
  @Output() complete = new EventEmitter<void>();

  formGroup!: FormGroup;
  loading: boolean = false;
  completed: boolean = false;

  constructor(
    private surveyService: SurveyService,
    private messageService: MessageService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Check if the surveyId (new survey) or previdence (show survey) inputs have changed
    if((changes['surveyId'] && changes['surveyId'].firstChange) || changes['previdence']) {
      this.init();
    }
  }

  init(): void {
    this.formGroup = new FormGroup({
      foresight: new FormControl(this.previdence?.foresight, Validators.required),
      objectives: new FormControl(this.previdence?.objectives, Validators.required),
      knowledge1: new FormControl(this.previdence?.knowledge1 || false),
      possession1: new FormControl(this.previdence?.possession1 || false),
      knowledge2: new FormControl(this.previdence?.knowledge2 || false),
      possession2: new FormControl(this.previdence?.possession2 || false),
      knowledge3: new FormControl(this.previdence?.knowledge3 || false),
      possession3: new FormControl(this.previdence?.possession3 || false),
      knowledge4: new FormControl(this.previdence?.knowledge4 || false),
      possession4: new FormControl(this.previdence?.possession4 || false),
      knowledge5: new FormControl(this.previdence?.knowledge5 || false),
      operation: new FormControl(this.previdence?.operation, Validators.required),
      investment: new FormControl(this.previdence?.investment, Validators.required),
      investmentFinancial: new FormControl(this.previdence?.investmentFinancial, Validators.required),
      familyIncome: new FormControl(this.previdence?.familyIncome, Validators.required),
      familyIncomeSource: new FormControl(this.previdence?.familyIncomeSource, Validators.required),
      incomeSavings: new FormControl(this.previdence?.incomeSavings || 0, [Validators.required, Validators.min(1), Validators.max(100)]),
      riskPropensity: new FormControl(this.previdence?.riskPropensity, Validators.required),
      riskTolerance: new FormControl(this.previdence?.riskTolerance, Validators.required),
      riskChoice: new FormControl(this.previdence?.riskChoice, Validators.required),
      decorrelation: new FormControl(this.previdence?.decorrelation, Validators.required)
    });

    // Set default values for investmentFinancial based on financialHeritage
    const financialHeritage = this.ndgData?.financialHeritage;
    if(financialHeritage < 25000) {
      this.investmentFinancial?.setValue('A');
    } else if(financialHeritage <= 100000) {
      this.investmentFinancial?.setValue('B');
    } else if(financialHeritage <= 500000) {
      this.investmentFinancial?.setValue('C');
    } else {
      this.investmentFinancial?.setValue('D');
    }

    this.investmentFinancial?.disable();

    // Set default values for familyIncome based on averageIncome
    const averageIncome = this.ndgData?.averageIncome;
    if(averageIncome < 15000) {
      this.familyIncome?.setValue('A');
    } else if(averageIncome <= 30000) {
      this.familyIncome?.setValue('B');
    } else if(averageIncome <= 45000) {
      this.familyIncome?.setValue('C');
    } else if(averageIncome <= 70000) {
      this.familyIncome?.setValue('D');
    } else {
      this.familyIncome?.setValue('E');
    }

    this.familyIncome?.disable();

    if(!this.isInsert()) {
      this.completed = true;
      this.formGroup.disable();
    }
  }

  isInsert(): boolean {
    return this.previdence === undefined;
  }

  get foresight() { return this.formGroup.get('foresight') }
  get objectives() { return this.formGroup.get('objectives') }
  get knowledge1() { return this.formGroup.get('knowledge1') }
  get possession1() { return this.formGroup.get('possession1') }
  get knowledge2() { return this.formGroup.get('knowledge2') }
  get possession2() { return this.formGroup.get('possession2') }
  get knowledge3() { return this.formGroup.get('knowledge3') }
  get possession3() { return this.formGroup.get('possession3') }
  get knowledge4() { return this.formGroup.get('knowledge4') }
  get possession4() { return this.formGroup.get('possession4') }
  get knowledge5() { return this.formGroup.get('knowledge5') }
  get operation() { return this.formGroup.get('operation') }
  get investment() { return this.formGroup.get('investment') }
  get investmentFinancial() { return this.formGroup.get('investmentFinancial') }
  get familyIncome() { return this.formGroup.get('familyIncome') }
  get familyIncomeSource() { return this.formGroup.get('familyIncomeSource') }
  get incomeSavings() { return this.formGroup.get('incomeSavings') }
  get riskPropensity() { return this.formGroup.get('riskPropensity') }
  get riskTolerance() { return this.formGroup.get('riskTolerance') }
  get riskChoice() { return this.formGroup.get('riskChoice') }
  get decorrelation() { return this.formGroup.get('decorrelation') }
  
  hasFamilyIncomeSourceIncongruence(incongruenceType: string): boolean {
    return familyIncomeSourceIncongruence(this.ndgData?.occupation)(this.familyIncomeSource?.value) === incongruenceType;
  }

  hasError(field: AbstractControl | null, errorType: string | undefined = undefined) {
    let hasError = !!field && field.touched && !field.disabled && !field.valid;

    if(hasError && !!errorType) {
      return field?.getError(errorType);
    }

    return hasError;
  }

  onSubmit() {
    if(!this.formGroup.valid || this.completed) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.formGroup.disable();
    this.completed = true;
    this.loading = true;

    const payload: SurveyNewPrevidenceRequest = {
      foresight: this.foresight?.value,
      objectives: this.objectives?.value,
      knowledge1: this.knowledge1?.value,
      possession1: this.possession1?.value,
      knowledge2: this.knowledge2?.value,
      possession2: this.possession2?.value,
      knowledge3: this.knowledge3?.value,
      possession3: this.possession3?.value,
      knowledge4: this.knowledge4?.value,
      possession4: this.possession4?.value,
      knowledge5: this.knowledge5?.value,
      operation: this.operation?.value,
      investment: this.investment?.value,
      investmentFinancial: this.investmentFinancial?.value,
      familyIncome: this.familyIncome?.value,
      familyIncomeSource: this.familyIncomeSource?.value,
      incomeSavings: this.incomeSavings?.value,
      riskPropensity: this.riskPropensity?.value,
      riskTolerance: this.riskTolerance?.value,
      riskChoice: this.riskChoice?.value,
      decorrelation: this.decorrelation?.value
    }

    this.surveyService.insertPrevidenceData(this.surveyId, payload).subscribe({
      next: () => {
        this.loading = false;
        this.complete.emit();
      },
      error: () => {
        this.messageService.error('survey.new.message.unknown-error');

        this.loading = false;
        this.completed = false;
        this.formGroup.enable();
      }
    });
  }
}
